import React from 'react';
import cn from 'classnames';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, Box } from '@mui/material';
import { OButton, OModal } from '@src/shared';
import styles from './DeclinedAttendeesModal.module.scss';
import { IAttendee } from '@src/webinar/types';
import CountryBadge from '@src/components/CountryBadge';
import { capitalize } from '@src/utils';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  declinedAttendees?: IAttendee[];
}

const TABLE_COLUMNS = [
  { id: 'name', label: 'Name' },
  { id: 'email', label: 'Email' },
  { id: 'company', label: 'Company' },
  { id: 'type', label: 'Type' },
  { id: 'country', label: 'Country and city' },
];

const DeclinedAttendeesModal = ({ isOpen, onClose, declinedAttendees = [] }: IProps) => {
  const renderCountryBadgeWithCity = (attendee: IAttendee) => {
    if (!attendee) return null;
    if (!attendee.city && !attendee.countryCode) return '-';
    const countryCode = attendee.countryCode ? attendee.countryCode.toLowerCase() : 'xx';
    return <CountryBadge countryCode={countryCode} countryName={attendee.city || ''} />;
  };

  return (
    <OModal
      isOpen={isOpen}
      onClose={onClose}
      classes={{
        container: styles.modalContainer,
        closeIconWrapper: styles.closeIconWrapper,
        closeIcon: styles.closeIcon,
      }}
    >
      {/* --- Title --- */}
      <h4 className={styles.modalTitle}>Declined Attendees</h4>

      {/* --- Table --- */}
      <TableContainer component={Paper} elevation={0} className={styles.tableContainerNoScroll}>
        <Table
          className={styles.table}
          stickyHeader
          sx={{
            borderCollapse: 'collapse',
          }}
        >
          <TableHead
            sx={{
              '& .MuiTableRow-root': {
                border: 'none',
              },
            }}
          >
            <TableRow>
              {TABLE_COLUMNS.map((column) => (
                <TableCell key={column.id} className={styles.headCell}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              border: '1px solid #E6E7E7',
              borderCollapse: 'collapse',
              '& .MuiTableRow-root': {
                borderBottom: '1px solid #E6E7E7',
              },
            }}
          >
            {declinedAttendees.map((row) => (
              <TableRow key={row._id} hover className={styles.bodyRow}>
                <TableCell className={styles.bodyCell}>{row.fullName}</TableCell>
                <TableCell className={styles.bodyCell}>{row.email}</TableCell>
                <TableCell className={styles.bodyCell}>{row.company}</TableCell>
                <TableCell className={styles.bodyCell}>{capitalize(row.type.replace('_', '-'))}</TableCell>
                <TableCell className={styles.bodyCell}>
                  <Box display="flex" alignItems="center">
                    {renderCountryBadgeWithCity(row)}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* --- Footer action --- */}
      <Box display="flex" justifyContent="flex-end" pt={2}>
        <OButton size="default" variant="primary" text="Got it" onClick={onClose} />
      </Box>
    </OModal>
  );
};

export default DeclinedAttendeesModal;
